import React, { useState, useEffect, useCallback } from "react";
import BusinessTracingJsonCard from "../components/BusinessTracingJsonCard";
import InputCard from "../components/InputCard";
import { DeliveryOption } from "../models/DeliveryOptionsModel";
import { getDeliveryOptions } from "../api/TestDeliveryOptions";
import DeliveryOptionsCard from "../components/DeliveryOptionsCard";
import './TestDeliveryOptionsPage.scss';
import { getBusinessTracingEvents, TraceEvent } from "../api/BusinessTracing";
import { Alert } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";

const TestDeliveryOptionsPage = (props: any) => {
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[] | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [showTraces, setShowTraces] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [traceEvents, setTraceEvents] = useState<TraceEvent[] | null>(null);
  const [traceId, setTraceId] = useState<string | undefined>();

  const { accounts }: any = useMsal();
  // Note: roles is defined but not currently used in the return JSX
  var roles = accounts[0].idTokenClaims.roles as Array<string>;

  /**
   * Fetches business trace events using a length-comparison polling strategy.
   * 1. Initial wait of 3 seconds.
   * 2. Poll every 1 second.
   * 3. Continue polling as long as result.length keeps increasing.
   * 4. Stop when two consecutive polls return the same amount of data.
   */
  const getBusinessTrace = useCallback(async (id?: string) => {
    const currentTraceId = id ?? traceId;
    if (!currentTraceId) return;

    try {
      setTraceLoading(true);
      
      // Step 1: Initial wait of 3 seconds
      await new Promise(resolve => setTimeout(resolve, 3000));

      let lastCount = 0;
      let polling = true;
      let totalPolls = 0;
      const maxPolls = 30; // 30 second timeout safeguard

      while (polling && totalPolls < maxPolls) {
        const result = await getBusinessTracingEvents(currentTraceId);
        const currentCount = result ? result.length : 0;

        if (currentCount > 0) {
          setTraceEvents(result);
        }

        // If the number of events has increased, we wait and poll again
        // If the count is the same as last time (and we have data), we stop.
        if (currentCount > 1 && currentCount === lastCount) {
          polling = false;
        } else {
          lastCount = currentCount;
          totalPolls++;
          // Step 2: Wait 1 second before checking for new data
          await new Promise(resolve => setTimeout(resolve, 1000));
        }        
      }
    } catch (err: any) {
      handleSetError(getErrorMessage(err));
    } finally {
      setTraceLoading(false);
    }
  }, [traceId]);

  /**
   * Effect: Automatically triggers a trace refresh whenever a new traceId is set
   */
  useEffect(() => {
    if (traceId) {
      getBusinessTrace(traceId);
    }
  }, [traceId, getBusinessTrace]);

  const handleStartLoading = () => {
    setOptionsLoading(true);
  };

  const showTracesToggle = () => {
    setShowTraces(!showTraces);
  };

  const handleStopLoading = () => {
    setOptionsLoading(false);
  };

  const handleSetError = (error: string) => {
    if (typeof error !== "string") {
      error = "Error happened";
    }
    setError(error);
  };

  const handleClearError = () => {
    setError(null);
  };

  const handleClearDeliveryOptions = () => {
    setDeliveryOptions(null);
    setTraceEvents(null);
  };

  const handleRefreshTrace = () => {
    getBusinessTrace();
  };

  const handleGetDeliveryOptions = async (jsonInput: string) => {
    handleStartLoading();
    handleClearError();
    setDeliveryOptions(null);
    setTraceEvents(null); // Reset trace events for the new request
    
    const newTraceId = crypto.randomUUID().replaceAll('-', '');
    setTraceId(newTraceId);

    try {
      const result = await getDeliveryOptions(JSON.parse(jsonInput), newTraceId);
      const resultOptions = result.flattenedDeliveryOptions.map(
        (option: any) =>
        ({
          factoryCode: option.factoryCode,
          shipmentNumber: option.shipmentNumber,
          rowSpan: option.rowSpan,
          products: option.products,
          optionNumber: option.optionNumber,
          shipDate: option.shipDate,
          deliveryDate: option.deliveryDate,
          deliveryMethod: option.deliveryMethod,
          deliveryType: option.deliveryType,
          carrierName: option.carrierName,
          iWaysCarrierCode: option.iWaysCarrierCode,
          pudoNetworkId: option.pudoNetworkId,
          deliveryMethodWithNetwork: { method: option.deliveryMethod, networkId: option.pudoNetworkId }
        } as DeliveryOption));
      
      setDeliveryOptions(resultOptions);
      // Trace loading is now handled by the useEffect watching traceId
    } catch (err: any) {
      handleSetError(getErrorMessage(err));
    } finally {
      handleStopLoading();
    }
  };

  const getErrorMessage = (err: any) => {
    let errorMessage = err.message || "An error occurred while getting the Delivery Options";
    const error = err?.response?.data?.Message;
    if (error) {
      errorMessage = err?.response?.data?.Message;
      try {
        let errorObj = JSON.parse(errorMessage);
        errorObj = JSON.parse(errorObj.Message);
        errorMessage = errorObj.Message;
      } catch { }
    }
    else if (err?.response?.data && typeof err?.response?.data === "string") {
      errorMessage = err?.response?.data;
    }
    else if (err?.response?.data?.title && typeof err?.response?.data?.title === "string") {
      errorMessage = err?.response?.data?.title;
    }
    return errorMessage;
  };

  return (
    <>
      <div className="row">
        <div className="col-md-4">
          <InputCard
            onGetDeliveryOptions={handleGetDeliveryOptions}
            onStartLoading={handleStartLoading}
            onStopLoading={handleStopLoading}
            onSetError={handleSetError}
            onClearError={handleClearError}
            onClearResult={handleClearDeliveryOptions}
          />
        </div>

        <div className="col-md-8">
          <DeliveryOptionsCard data={deliveryOptions} isLoading={optionsLoading} />
          {error && (
            <Alert variant="danger" onClose={handleClearError} dismissible>
              <Alert.Heading>{error}</Alert.Heading>
            </Alert>
          )}
          <div>
            <BusinessTracingJsonCard
              traceEvents={traceEvents}
              onRefreshTrace={handleRefreshTrace}
              showTracesToggle={showTracesToggle}
              isLoading={traceLoading}
              showTraces={showTraces}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TestDeliveryOptionsPage;